import { Component, OnInit, AfterViewInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  AiCoinBalance,
  AiCoinPack,
  AiCoinService,
  DEFAULT_AI_COIN_PACKS,
} from "src/app/Services/ai-coin.service";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { loadPayPalSdk } from "src/app/shared/utils/paypal-sdk.util";
import {
  clearCoinPurchaseReturnUrl,
  resolveCoinPurchaseReturnUrl,
  setCoinPurchaseReturnUrl,
} from "src/app/shared/utils/coin-purchase-return.util";
import { environment } from "src/environments/environment";

declare var paypal: any;

@Component({
  selector: "app-purchase-coins",
  templateUrl: "./purchase-coins.component.html",
  styleUrls: ["./purchase-coins.component.scss"],
})
export class PurchaseCoinsComponent implements OnInit, AfterViewInit {
  coinPacks: AiCoinPack[] = [...DEFAULT_AI_COIN_PACKS];
  coinBalance: AiCoinBalance | null = null;
  selectedPackId = "try";
  isLoadingBalance = false;
  purchaseInProgress = false;
  /** Where to send the user back after the purchase completes. */
  returnUrl: string | null = null;

  readonly paypalClientId =
    environment.paypalClientId ||
    "AVk8ODiI6Nh105imMWm_0NFp9mz6ofDjiA2Di3Usq2gaeZ66880LYfgBwjyGjuxnm_M1vsozkp0T2VXg";

  private paypalReady = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private aiCoinService: AiCoinService,
    private authModalService: AuthModalService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    const initialReturnUrl = resolveCoinPurchaseReturnUrl(
      this.route.snapshot.queryParamMap.get("returnUrl")
    );

    if (!userData) {
      const queryParams = new URLSearchParams({
        pack: this.route.snapshot.queryParamMap.get("pack") || "try",
      });
      if (initialReturnUrl) {
        queryParams.set("returnUrl", initialReturnUrl);
        setCoinPurchaseReturnUrl(initialReturnUrl);
      }
      this.authModalService.openModal(
        "registration",
        `/purchase-coins?${queryParams.toString()}`
      );
      return;
    }

    this.route.queryParamMap.subscribe((params) => {
      const pack = (params.get("pack") || "try").toLowerCase();
      if (this.coinPacks.some((p) => p.id === pack)) {
        this.selectedPackId = pack;
      }
      this.returnUrl = resolveCoinPurchaseReturnUrl(params.get("returnUrl"));
      if (this.returnUrl) {
        setCoinPurchaseReturnUrl(this.returnUrl);
      }
      if (this.paypalReady) {
        setTimeout(() => this.initializePayPalButtons(), 0);
      }
    });

    this.loadBalance();
  }

  ngAfterViewInit(): void {
    this.loadPayPalScript();
  }

  get selectedPack(): AiCoinPack {
    return (
      this.coinPacks.find((p) => p.id === this.selectedPackId) ||
      this.coinPacks[0]
    );
  }

  selectPack(packId: string): void {
    this.selectedPackId = packId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pack: packId },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
    if (this.paypalReady) {
      setTimeout(() => this.initializePayPalButtons(), 0);
    }
  }

  goToPricing(): void {
    this.router.navigate(["/pricing"], { fragment: "ai-coins" });
  }

  /** Returns the user to where they started (e.g. manual/smart create) with their draft intact. */
  private navigateBackAfterPurchase(): void {
    const target = resolveCoinPurchaseReturnUrl(this.returnUrl);
    if (!target) {
      return;
    }

    clearCoinPurchaseReturnUrl();
    setTimeout(() => {
      this.router.navigateByUrl(target);
    }, 1500);
  }

  private loadBalance(): void {
    this.isLoadingBalance = true;
    this.aiCoinService.getBalance().subscribe({
      next: (balance) => {
        this.coinBalance = balance;
        if (balance.packs?.length) {
          this.coinPacks = balance.packs;
        }
        this.isLoadingBalance = false;
      },
      error: () => {
        this.isLoadingBalance = false;
      },
    });
  }

  private loadPayPalScript(): void {
    loadPayPalSdk({
      clientId: this.paypalClientId,
      intent: "capture",
      currency: "GBP",
    })
      .then(() => {
        this.paypalReady = true;
        setTimeout(() => this.initializePayPalButtons(), 0);
      })
      .catch(() => {
        this.paypalReady = false;
        this.toastr.error(
          "PayPal checkout could not be loaded. Please refresh and try again.",
          "Payment unavailable"
        );
      });
  }

  private initializePayPalButtons(): void {
    if (!this.paypalReady || typeof paypal === "undefined") {
      return;
    }

    const container = document.getElementById("paypal-coin-pack-container");
    if (!container) {
      return;
    }

    container.innerHTML = "";
    const pack = this.selectedPack;
    const price = Number(pack.priceGbp);
    if (!Number.isFinite(price) || price <= 0) {
      this.toastr.error(
        "This coin pack price is invalid. Please refresh and try again.",
        "Payment unavailable"
      );
      return;
    }

    paypal
      .Buttons({
        style: {
          shape: "rect",
          color: "gold",
          layout: "vertical",
          label: "paypal",
        },
        createOrder: (_data: unknown, actions: any) => {
          return actions.order.create({
            purchase_units: [
              {
                description: `${pack.coins} AI coins — ${pack.name}`,
                amount: {
                  currency_code: "GBP",
                  value: price.toFixed(2),
                },
              },
            ],
          });
        },
        onApprove: (_data: unknown, actions: any) => {
          this.purchaseInProgress = true;
          return actions.order.capture().then((details: { id: string }) => {
            this.aiCoinService
              .purchase(pack.id, details.id)
              .subscribe({
                next: (balance) => {
                  this.coinBalance = balance;
                  this.purchaseInProgress = false;
                  this.toastr.success(
                    `${pack.coins} AI coins added to your account.`,
                    "Purchase complete"
                  );
                  this.navigateBackAfterPurchase();
                },
                error: () => {
                  this.purchaseInProgress = false;
                  this.toastr.error(
                    "Payment received but coins could not be credited. Contact support with your PayPal receipt."
                  );
                },
              });
          });
        },
        onError: () => {
          this.purchaseInProgress = false;
          this.toastr.error("PayPal checkout failed. Please try again.");
        },
        onCancel: () => {
          this.purchaseInProgress = false;
        },
      })
      .render("#paypal-coin-pack-container")
      .catch(() => {
        this.toastr.error(
          "PayPal button could not be displayed. Please refresh and try again.",
          "Payment unavailable"
        );
      });
  }
}
